import { useState, useEffect } from 'react';
import { subjectAPI, classAPI } from '../../utils/api';
import { toast } from 'react-toastify';
import {
  RiAddLine, RiEditLine, RiDeleteBinLine, RiBookLine,
  RiCloseLine, RiLoaderLine,
} from 'react-icons/ri';

const SubjectManagement = () => {
  const [subjects, setSubjects] = useState([]);
  const [classes, setClasses] = useState([]);
  const [showForm, setShowForm] = useState(false);
  const [formData, setFormData] = useState({ name: '', classId: '', code: '', description: '' });
  const [loading, setLoading] = useState(true);
  const [saving, setSaving] = useState(false);
  const [editingId, setEditingId] = useState(null);

  useEffect(() => { fetchData(); }, []);

  const fetchData = async () => {
    try {
      const [subjectsRes, classesRes] = await Promise.all([
        subjectAPI.getAllSubjects(),
        classAPI.getAllClasses(),
      ]);
      setSubjects(subjectsRes.data.subjects || []);
      setClasses(classesRes.data.classes || []);
    } catch {
      toast.error('Failed to load data');
    } finally {
      setLoading(false);
    }
  };

  const handleSubmit = async (e) => {
    e.preventDefault();
    setSaving(true);
    try {
      if (editingId) {
        await subjectAPI.updateSubject(editingId, formData);
        toast.success('Subject updated successfully');
      } else {
        await subjectAPI.createSubject(formData);
        toast.success('Subject created successfully');
      }
      setFormData({ name: '', classId: '', code: '', description: '' });
      setShowForm(false);
      setEditingId(null);
      fetchData();
    } catch (err) {
      toast.error(err.response?.data?.message || 'Failed to save subject');
    } finally {
      setSaving(false);
    }
  };

  const handleDelete = async (id) => {
    if (!window.confirm('Are you sure? This will delete all associated chapters.')) return;
    try {
      await subjectAPI.deleteSubject(id);
      toast.success('Subject deleted');
      fetchData();
    } catch {
      toast.error('Failed to delete subject');
    }
  };

  const openCreate = () => {
    setEditingId(null);
    setFormData({ name: '', classId: '', code: '', description: '' });
    setShowForm(true);
  };

  if (loading) return (
    <div className="flex items-center justify-center h-64">
      <div className="w-8 h-8 spinner" />
    </div>
  );

  return (
    <div className="p-6 max-w-6xl mx-auto animate-fade-in-up">
      {/* Header */}
      <div className="flex flex-col sm:flex-row sm:items-center justify-between gap-4 mb-6">
        <div>
          <h1 className="text-xl font-bold text-slate-800">Subjects</h1>
          <p className="text-sm text-slate-500 mt-0.5">{subjects.length} subject{subjects.length !== 1 ? 's' : ''}</p>
        </div>
        <button onClick={openCreate} className="btn-primary whitespace-nowrap">
          <RiAddLine className="w-4 h-4" /> Add Subject
        </button>
      </div>

      {/* Form panel */}
      {showForm && (
        <div className="card p-6 mb-6 animate-fade-in-up border-l-4 border-blue-500">
          <div className="flex items-center justify-between mb-5">
            <h2 className="text-base font-semibold text-slate-800">
              {editingId ? 'Edit Subject' : 'Create New Subject'}
            </h2>
            <button onClick={() => setShowForm(false)} className="btn-icon btn-ghost text-slate-400">
              <RiCloseLine className="w-5 h-5" />
            </button>
          </div>
          <form onSubmit={handleSubmit} className="grid grid-cols-1 sm:grid-cols-2 gap-4 max-w-2xl">
            <div>
              <label className="label">Class *</label>
              <select className="input" value={formData.classId}
                onChange={e => setFormData({ ...formData, classId: e.target.value })} required>
                <option value="">Select class</option>
                {classes.map(cls => <option key={cls._id} value={cls._id}>{cls.name}</option>)}
              </select>
            </div>
            <div>
              <label className="label">Subject Name *</label>
              <input className="input" type="text" placeholder="e.g. Mathematics" value={formData.name}
                onChange={e => setFormData({ ...formData, name: e.target.value })} required />
            </div>
            <div>
              <label className="label">Subject Code</label>
              <input className="input" type="text" placeholder="e.g. MATH-10" value={formData.code}
                onChange={e => setFormData({ ...formData, code: e.target.value })} />
            </div>
            <div className="sm:col-span-2">
              <label className="label">Description</label>
              <textarea className="input resize-none" placeholder="Optional description" value={formData.description}
                onChange={e => setFormData({ ...formData, description: e.target.value })} rows={2} />
            </div>
            <div className="sm:col-span-2 flex gap-3">
              <button type="submit" disabled={saving} className="btn-primary flex-1">
                {saving ? <><RiLoaderLine className="w-4 h-4 animate-spin" /> Saving…</> : editingId ? 'Update Subject' : 'Create Subject'}
              </button>
              <button type="button" onClick={() => setShowForm(false)} className="btn-secondary flex-1">Cancel</button>
            </div>
          </form>
        </div>
      )}

      {/* Table */}
      {subjects.length === 0 ? (
        <div className="card p-12 text-center">
          <RiBookLine className="w-10 h-10 text-slate-300 mx-auto mb-3" />
          <p className="text-slate-500 font-medium">No subjects yet</p>
          <p className="text-slate-400 text-sm mt-1">Create a class first, then add subjects</p>
        </div>
      ) : (
        <div className="card overflow-hidden">
          <div className="overflow-x-auto">
            <table className="w-full text-sm">
              <thead className="bg-slate-50 border-b border-slate-200">
                <tr>
                  <th className="px-5 py-3.5 text-left text-xs font-semibold text-slate-500 uppercase tracking-wide">Subject</th>
                  <th className="px-5 py-3.5 text-left text-xs font-semibold text-slate-500 uppercase tracking-wide">Class</th>
                  <th className="px-5 py-3.5 text-left text-xs font-semibold text-slate-500 uppercase tracking-wide hidden sm:table-cell">Code</th>
                  <th className="px-5 py-3.5 text-right text-xs font-semibold text-slate-500 uppercase tracking-wide">Actions</th>
                </tr>
              </thead>
              <tbody className="divide-y divide-slate-100">
                {subjects.map(s => (
                  <tr key={s._id} className="hover:bg-slate-50 transition-colors">
                    <td className="px-5 py-3.5">
                      <div className="flex items-center gap-3">
                        <div className="w-7 h-7 bg-violet-100 rounded-lg flex items-center justify-center flex-shrink-0">
                          <RiBookLine className="w-3.5 h-3.5 text-violet-600" />
                        </div>
                        <span className="font-semibold text-slate-800">{s.name}</span>
                      </div>
                    </td>
                    <td className="px-5 py-3.5">
                      <span className="badge badge-blue">{s.classId?.name || 'N/A'}</span>
                    </td>
                    <td className="px-5 py-3.5 hidden sm:table-cell">
                      {s.code ? (
                        <span className="font-mono text-xs bg-slate-100 text-slate-700 px-2 py-1 rounded-md">{s.code}</span>
                      ) : <span className="text-slate-400">—</span>}
                    </td>
                    <td className="px-5 py-3.5">
                      <div className="flex items-center justify-end gap-1">
                        <button
                          onClick={() => {
                            setEditingId(s._id);
                            setFormData({ name: s.name, classId: s.classId?._id || '', code: s.code || '', description: s.description || '' });
                            setShowForm(true);
                          }}
                          className="btn-icon btn-ghost text-blue-600 hover:bg-blue-50"
                        >
                          <RiEditLine className="w-4 h-4" />
                        </button>
                        <button onClick={() => handleDelete(s._id)} className="btn-icon btn-ghost text-red-600 hover:bg-red-50">
                          <RiDeleteBinLine className="w-4 h-4" />
                        </button>
                      </div>
                    </td>
                  </tr>
                ))}
              </tbody>
            </table>
          </div>
        </div>
      )}
    </div>
  );
};

export default SubjectManagement;
